import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircularProgress, Alert, Chip } from '@mui/material';
import { getReportJobs } from '../../api/reports';
import Pagination from '../Paginations';
import { formatDistanceToNow } from 'date-fns';

const AdminReportJobs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Get admin user ID from localStorage
  const uid = localStorage.getItem('uid');

  // Query for fetching report jobs
  const {
    data: jobsData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['report_jobs', uid],
    queryFn: () => getReportJobs(uid),
    enabled: !!uid, // Only run query when userId is available
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Format date to relative time (e.g., "2 hours ago")
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return dateString;
    }
  };

  // Get status color based on job status
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Pagination logic
  const jobs = jobsData?.jobs || [];
  const totalPages = Math.ceil(jobs.length / itemsPerPage);
  const currentJobs = jobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="admin-page py-5">
      <div className='container'>
        <h1 className='dash-title'>Report Jobs Monitor</h1>
        <div className="row">
          <div className="col-12 mt-4">
            <div className="dash-card">
              <div className="dash-card-heading">
                <h2 className="dash-card-title">Report Processing Jobs</h2>
                <button 
                  className="dash-card-heading-btn" 
                  onClick={() => refetch()}
                >
                  <i className="fi fi-rr-refresh me-2"></i>
                  Refresh
                </button>
              </div>
              
              {/* Loading State */}
              {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
                  <CircularProgress />
                </div>
              )}

              {/* Error State */}
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ margin: '20px 0' }}
                >
                  {error.message || 'An error occurred while fetching report jobs'}
                </Alert>
              )}

              {/* Empty State - No Jobs */}
              {!isLoading && !error && jobs.length === 0 && (
                <Alert 
                  severity="info" 
                  sx={{ margin: '20px 0' }}
                >
                  No report jobs found. Jobs will appear here when reports are processed.
                </Alert>
              )}

              {/* Data Table */}
              {!isLoading && !error && jobs.length > 0 && (
                <div className="table-responsive mt-3">
                  <table className="table dash-table">
                    <thead>
                      <tr>
                        <td>Job ID</td>
                        <td>Job Name</td>
                        <td>Status</td>
                        <td>Created</td>
                        <td>Last Updated</td>
                      </tr>
                    </thead>
                    <tbody>
                      {currentJobs.map((job) => (
                        <tr key={job.id}>
                          <td><div className='table-item'>{job.id}</div></td>
                          <td><div className='table-item'>{JSON.parse(job.fileUrl)?.monthYear || 'N/A'}</div></td>
                          <td>
                            <div className='table-item'>
                              <Chip 
                                label={job.status} 
                                color={getStatusColor(job.status)}
                                size="small"
                              />
                            </div>
                          </td>
                          <td><div className='table-item'>{formatDate(job.createdAt)}</div></td>
                          <td><div className='table-item'>{formatDate(job.updatedAt)}</div></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {!isLoading && !error && jobs.length > 0 && (
                <Pagination 
                  currentPage={currentPage} 
                  totalPages={totalPages} 
                  handlePageChange={handlePageChange} 
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReportJobs; 